/**
 * useInsertAfter — shared helper for the "+ button / slash menu → insert block"
 * flow used by both List and Notes views.
 *
 * Hierarchy is two tiers: Epic > Story.
 *   - Inserting a story under a story anchor inherits the anchor's epic parent.
 *   - Inserting a story under an epic anchor uses that epic as parent.
 *   - Inserting an epic is always root-level (no parent).
 *   - If no sensible parent can be inferred for a story, throw so the caller
 *     can surface a user-visible error rather than silently posting a 400.
 */

import { useCallback } from 'react'
import type { Requirement } from '@ispot/shared'
import type { BlockType } from '@/components/requirements/SlashMenu'
import type { CreateBlockInput } from '@/components/requirements/AdderBlock'

type OnCreate = (input: CreateBlockInput) => Promise<{ id: string }>
type OnReorder = (parentId: string | null, orderedIds: string[]) => Promise<void>

interface Options {
  requirements: Requirement[]
  onCreate: OnCreate
  onReorder: OnReorder
}

/**
 * Build a Set of requirement ids that are `anchor` OR descendants of it,
 * so parent lookups stay scoped.
 */
function descendantIds(all: Requirement[], anchorId: string): Set<string> {
  const childrenByParent = new Map<string, Requirement[]>()
  for (const r of all) {
    if (!r.parentId) continue
    const arr = childrenByParent.get(r.parentId) ?? []
    arr.push(r)
    childrenByParent.set(r.parentId, arr)
  }
  const out = new Set<string>([anchorId])
  const queue = [anchorId]
  while (queue.length) {
    const id = queue.shift()!
    const kids = childrenByParent.get(id) ?? []
    for (const k of kids) {
      if (!out.has(k.id)) { out.add(k.id); queue.push(k.id) }
    }
  }
  return out
}

export function useInsertAfter({ requirements, onCreate, onReorder }: Options) {
  return useCallback(async (
    anchor: Requirement,
    newType: BlockType,
    personaSessionId?: string,
  ): Promise<{ id: string }> => {
    let parentId: string | undefined

    if (newType === 'story') {
      if (anchor.type === 'epic' || anchor.type === 'nfr-category') {
        parentId = anchor.id
      } else if (anchor.type === 'story') {
        parentId = anchor.parentId ?? undefined
      }
    }
    // newType === 'epic' → root (parentId stays undefined)

    // If we couldn't find a parent epic for a story, bail with a clear error so
    // the caller can toast. This prevents silent 400s when no epic exists yet.
    if (newType === 'story' && !parentId) {
      throw new Error(
        `Cannot insert a story: no parent epic found for ${anchor.type} "${anchor.name}". Add an Epic first.`,
      )
    }

    const { id } = await onCreate({ type: newType, name: '', parentId, personaSessionId })

    // Same-type insertion → reorder siblings so the new block sits right after the anchor.
    if (newType === anchor.type) {
      const siblings = requirements
        .filter(r => r.type === newType && (r.parentId ?? null) === (parentId ?? null) && r.id !== id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
      const anchorIdx = siblings.findIndex(s => s.id === anchor.id)
      if (anchorIdx >= 0) {
        const ordered = [...siblings]
        ordered.splice(anchorIdx + 1, 0, { id } as Requirement)
        await onReorder(parentId ?? null, ordered.map(s => s.id))
      }
    }

    return { id }
  }, [requirements, onCreate, onReorder])
}

// Expose the helper for other feature callers (e.g. keyboard shortcut handlers).
export { descendantIds }
